import { ItemProps } from '@progress/kendo-react-dropdowns';
import { Tooltip } from '@progress/kendo-react-tooltip';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CircularIconButton from 'src/components/design-system/CircularIconButton';
import TreeDropDown from 'src/components/design-system/DropDownTree';
import useViewCheckbook from 'src/hooks/useViewCheckbook';
import { processTreeData } from 'src/utils/treeDataOperations';
import { initialYearCheckbookDataState } from './ViewCheckbookContext';

const selectField = 'selected';
const expandField = 'expanded';
const dataItemKey = 'id';
const textField = 'content';
const subItemsField = 'items';
const fields = { selectField, expandField, dataItemKey, subItemsField };

const YearDropdown = () => {
  const {
    fiscalYear,
    dropDownYearValues,
    dropDownYearValue,
    setDropDownYearValue,
    isDropDownLoading,
    setYearCheckbookDataState,
    confirmBeforeProceeding,
    dropDownIndexByYear,
  } = useViewCheckbook();

  const onLeftBtnYearDropDwnClick = useCallback(() => {
    confirmBeforeProceeding(() => {
      setDropDownYearValue(dropDownYearValues[dropDownIndexByYear + 1]);
      setYearCheckbookDataState((prev) => ({
        ...initialYearCheckbookDataState,
        take: prev.take,
      }));
    });
  }, [
    confirmBeforeProceeding,
    dropDownIndexByYear,
    dropDownYearValues,
    setDropDownYearValue,
    setYearCheckbookDataState,
  ]);

  const onRightBtnYearDropDwnClick = useCallback(() => {
    confirmBeforeProceeding(() => {
      setDropDownYearValue(dropDownYearValues[dropDownIndexByYear - 1]);
      setYearCheckbookDataState((prev) => ({
        ...initialYearCheckbookDataState,
        take: prev.take,
      }));
    });
  }, [
    confirmBeforeProceeding,
    dropDownIndexByYear,
    dropDownYearValues,
    setDropDownYearValue,
    setYearCheckbookDataState,
  ]);

  const isLeftBtnYearDropDwnDisabled: boolean = useMemo(() => {
    if (dropDownIndexByYear === dropDownYearValues?.length - 1) {
      return true;
    } else return false;
  }, [dropDownIndexByYear, dropDownYearValues?.length]);

  const isRightBtnYearDropDwnDisabled: boolean = useMemo(() => {
    if (dropDownIndexByYear === 0) {
      return true;
    } else return false;
  }, [dropDownIndexByYear]);

  const { t } = useTranslation();
  const [expanded, setExpanded] = useState([] as number[]);
  const [dropDownOpened, setDropDownOpened] = useState<boolean>(false);

  const fixExpandedByglPeriod = useCallback(() => {
    setExpanded(fiscalYear && dropDownYearValue?.fiscalYear ? [dropDownYearValue.fiscalYear] : []);
  }, [dropDownYearValue?.fiscalYear, fiscalYear]);

  const filterOutChildItems = (data: any) =>
    data.filter((item: any) => !item[subItemsField] || item[subItemsField].length === 0);

  const onChange = useCallback(
    (event: any) => {
      const { value = {} } = event;
      const { fiscalYear = 0 } = value || {};
      confirmBeforeProceeding(() => {
        setDropDownYearValue(value);
        setExpanded((prev) => [...prev, fiscalYear]);
        setYearCheckbookDataState((prev: any) => ({
          ...initialYearCheckbookDataState,
          take: prev.take,
        }));
        setDropDownOpened(false);
      });
    },
    [confirmBeforeProceeding, setDropDownYearValue, setYearCheckbookDataState]
  );

  const treeData = useMemo(
    () =>
      processTreeData(
        filterOutChildItems(dropDownYearValues),
        {
          expanded,
          dropDownYearValue,
        },
        fields
      ),
    [dropDownYearValues, expanded, dropDownYearValue]
  );

  const Item = useCallback(
    (props: ItemProps) => {
      const { content } = props.item;
      const isSelected = props.item.fiscalYear === dropDownYearValue?.fiscalYear;

      return (
        <span
          style={
            isSelected
              ? {
                  color: '#ffffff',
                  backgroundColor: '#0E80B4',
                }
              : {}
          }
          className={`${isSelected ? 'cursor-not-allowed p-1 ' : ''}`}
        >
          {content}
        </span>
      );
    },
    [dropDownYearValue?.fiscalYear]
  );

  return (
    <Tooltip anchorElement="target" position="bottom" parentTitle>
      <div className="flex items-center flex-wrap">
        <span className="text-xs mr-2">{t('Select a Fiscal Year')}</span>
        <div>
          <span
            title={
              isLeftBtnYearDropDwnDisabled ? 'No Previous Fiscal Year' : 'Previous Fiscal Year'
            }
          >
            <CircularIconButton
              icon="arrow-end-left"
              disabled={isLeftBtnYearDropDwnDisabled}
              onClick={onLeftBtnYearDropDwnClick}
            />
          </span>
          <TreeDropDown
            expanded={expanded}
            setExpanded={setExpanded}
            dropDownOpened={dropDownOpened}
            setDropDownOpened={setDropDownOpened}
            id="year-dropdown"
            style={{ width: '260px' }}
            data={treeData}
            value={dropDownYearValue}
            placeholder={t('Please select ...')}
            textField={textField}
            dataItemKey={dataItemKey}
            selectField={selectField}
            expandField={expandField}
            loading={isDropDownLoading}
            setInitialExpandValue={fixExpandedByglPeriod}
            onChange={onChange}
            key={dropDownYearValue?.fiscalYear}
            item={Item}
            className="checkbook-period-dropdown"
            popupSettings={{
              className: '!w-[270px] !max-h-[400px] border-gray-500  border-opacity-10 border-2',
            }}
          />
          <span
            title={isRightBtnYearDropDwnDisabled ? 'No Future Fiscal Year' : 'Next Fiscal Year'}
          >
            <CircularIconButton
              icon="arrow-end-right"
              disabled={isRightBtnYearDropDwnDisabled}
              onClick={onRightBtnYearDropDwnClick}
            />
          </span>
        </div>
      </div>
    </Tooltip>
  );
};

export default YearDropdown;
